import React from 'react';
import Yup from '~/util/yup';
import { addAdvanceDirectiveOptions, AdvanceDirectiveTypeOptions, editAdvanceDirectiveOptions, getAdvanceDirectivesOptions } from '@generated';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import type { AdvanceDirectiveModel } from '../types';
import { PatientDocumentSelectField } from '../../documents/controls/PatientDocumentSelectField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { PatientContactSelectField } from '../../contacts/controls/PatientContactSelectField';
import { useQuery } from '@tanstack/react-query';
import { FormModal } from '~/shared/forms/FormModal';

const validationSchema = Yup.object().shape({
	advanceDirectiveTypeId: Yup.string().label('Directive Type').nullable().required(),
	awaitingDocumentation: Yup.boolean().label('Awaiting Documentation').nullable(),
	attachedDocumentId: Yup.string()
		.nullable()
		.label('Attached Documentation')
		.test('doc-required-check', 'Documentation required.', function () {
			const { advanceDirectiveTypeId, awaitingDocumentation, attachedDocumentId } = this.parent;
			const advanceDirectiveType = AdvanceDirectiveTypeOptions.find(x => x.id === Number(advanceDirectiveTypeId));
			if (!advanceDirectiveType) {
				return true;
			}
			if (awaitingDocumentation) {
				return true;
			}
			if (advanceDirectiveType.requiresDocumentation) {
				return attachedDocumentId !== null;
			}
			return true;
		}),
	attachedContactId: Yup.string().label('Attached Contact').nullable(),
	notes: Yup.string().label('Notes').nullable()
});

export function AdvanceDirectiveFormModal({
	stayId,
	existingDirective,
	quickAddAdvancedDirectiveTypeId,
	closeModal
}: {
	stayId: string;
	existingDirective?: AdvanceDirectiveModel;
	quickAddAdvancedDirectiveTypeId?: number;
	closeModal: () => void;
}) {
	const { data: directives, isLoading } = useQuery(getAdvanceDirectivesOptions(stayId));
	if (isLoading || !directives) {
		return null;
	}

	const initialValues = existingDirective
		? { ...existingDirective, advanceDirectiveId: existingDirective.id }
		: {
				stayId,
				advanceDirectiveTypeId: quickAddAdvancedDirectiveTypeId,
				attachedDocumentId: null,
				attachedContactId: null,
				notes: null,
				awaitingDocumentation: false
		  };

	return (
		<FormModal
			title={`${existingDirective ? 'Edit' : 'Add'} Directive`}
			initialValues={initialValues}
			saveMutation={{
				...(existingDirective ? editAdvanceDirectiveOptions : addAdvanceDirectiveOptions),
				invalidateQueryKeys: [getAdvanceDirectivesOptions(stayId).queryKey]
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			{formHelpers => {
				const advancedCareDirectiveTypeId = formHelpers?.values?.advanceDirectiveTypeId;
				const selectedType = advancedCareDirectiveTypeId
					? AdvanceDirectiveTypeOptions.find(x => x.id === Number(advancedCareDirectiveTypeId))
					: null;
				return (
					<div className="grid grid-cols-1 gap-4">
						<SelectFormField
							name="advanceDirectiveTypeId"
							options={AdvanceDirectiveTypeOptions}
							isReadOnly={Boolean(existingDirective) || Boolean(quickAddAdvancedDirectiveTypeId)}
						/>
						{selectedType && selectedType.requiresDocumentation && selectedType.storedDocumentTypeId && (
							<>
								<PatientDocumentSelectField
									name="attachedDocumentId"
									description="Select a document to attach to this directive. If document is not present check 'Awaiting Documentation' and when verified, a task will be created to upload it when verified."
									stayId={stayId}
									storedDocumentTypeId={selectedType.storedDocumentTypeId}
								/>
								<CheckboxFormField name="awaitingDocumentation" hideLabel />
							</>
						)}
						{selectedType && selectedType.requiresContact && (
							<PatientContactSelectField
									name="attachedContactId"
									noContactsLabel="Please add contact before proceeding"
									stayId={stayId}
								/>
						)}
						<TextAreaFormField name="notes" />
					</div>
				);
			}}
		</FormModal>
	);
}
